/**
 * Tanjong Life · L2 · the same claim, asked of the agent that runs on this platform.
 *
 * Scoped to `claims-fraud-analyst`. The beat is the contrast with L1: the same 78, but
 * every term, its source, its sign, and the version of the indicator set the terms came
 * from — and then a lesson it wrote from a claim that was cleared last week.
 *
 * The nine signed weights below are `registry.INDICATORS`, in registry order, transcribed
 * at authoring time; a scenario chunk cannot import the registry (the demo bundle must not
 * pull `scripts/**` into the app), so the mirror is pinned by a test instead. The minus
 * signs are U+2212, matching the inbox card that prints the same nine terms.
 *
 * No mutation steps: answering a question about a score must not change one.
 */
import type { Scenario } from '../director.js';

export const tanjongChatAnalyst: Scenario = {
  id: 'tanjong-chat-analyst',
  orgId: 'tanjong-life',
  agentPk: 'claims-fraud-analyst',
  keywords: ['score 78', 'nine indicators', 'what did you learn', 'frd-3309'],
  steps: [
    {
      do: 'thinking',
      steps: [
        {
          tool: {
            name: 'claims.read',
            input: 'claim=CLM-3302 · include history',
            output: 'hospitalisation · SGD 42,900 · 4 claims from one clinic in 91 days',
          },
        },
        {
          tool: {
            name: 'knowledge.read',
            input: 'FRAUD-IND-SET · issue in force on the claim date, not today',
            output: 'v4 · nine indicators, four of them negative',
          },
        },
        {
          thought:
            'Re-derive the number from the terms rather than quoting the number I published. If the terms do not reconstruct it, the explanation is wrong and the score is what has to give.',
        },
      ],
      delay: 700,
    },
    {
      do: 'say',
      text: 'Seventy-eight, and here is every term that made it. Provider concentration +30, the treating clinic on four unrelated claims in ninety-one days. Treatment code identity +26, an identical code sequence across all four. Proximity to the waiting period +22, filed eleven days after it ended. Invoice pattern +15, round-number totals on three of the four. First-party corroboration +11, no independent report on file. Against referral: claim history −11, fourteen years of continuous cover with no prior claim. Identity and residency checks −8, clean. Payment behaviour −4, no arrears in fourteen years. Channel −3, a long-standing agency with no adverse history. Add them: +30 +26 +22 +15 +11 −11 −8 −4 −3 = 78, against a referral threshold of 70 on version four of the set.',
      sources: [
        'FRAUD-IND-SET · v4 · in force on the claim date',
        'SIU referral threshold · v4 · 70 · Financial Crime desk',
      ],
      delay: 1400,
    },
    {
      do: 'say',
      text: 'The four terms that argue AGAINST referral are the ones worth watching. They survive into the explanation and they are why this is a 78 rather than a 104. An explanation that only carried the terms pointing one way would be an argument, not a score.',
      delay: 1200,
    },
    {
      do: 'say',
      text: 'And I learned something on 20 August that changed how I read this one. FRD-3309 scored 81 on the same provider-concentration signal and the desk cleared it: the clinic had joined the panel four months earlier, so a cluster of claims through it was the panel working, not a pattern. The lesson is recorded against the claim that produced it, and it is why provider concentration on a newly on-boarded clinic now carries the panel-tenure question with it. I did not lower the weight — that is the desk’s decision, not mine.',
      sources: ['FRD-3309 · cleared at 81 · 20 August', 'Lesson · panel tenure qualifies concentration'],
      delay: 1300,
    },
  ],
};
